import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { useT } from "@magic-md/i18n/react";
import type { LogEntry } from "../../store/log-store";
import { getThemeAccentStyle } from "../../theme/runtime/theme-accents";
import { formatTime, levelConfig } from "./log-config";

export function LogEntryRow({ entry }: { entry: LogEntry }) {
    const t = useT("editor");
    const [expanded, setExpanded] = useState(false);
    const cfg = levelConfig[entry.level];
    const Icon = cfg.icon;
    const hasExtra = !!(entry.stack || entry.data);
    const accentStyle = getThemeAccentStyle(cfg.accent);
    const errorAccentStyle = getThemeAccentStyle("error");

    return (
        <div
            className={`group flex flex-col border-b border-border/30 last:border-0 hover:bg-muted/30 ${entry.level === "error" ? "theme-accent-banner" : ""
                }`}
            style={entry.level === "error" ? errorAccentStyle : undefined}
        >
            <button
                type="button"
                className="flex w-full items-start justify-start gap-1.5 px-3 py-1 text-left cursor-pointer min-h-[26px] disabled:cursor-default disabled:opacity-100"
                onClick={() => hasExtra && setExpanded(!expanded)}
                onKeyDown={(e) => {
                    if (!hasExtra) return;
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpanded((v) => !v);
                    }
                }}
                disabled={!hasExtra}
            >
                {/* Time */}
                <span className="text-[10px] font-mono text-muted-foreground/60 shrink-0 pt-[2px] w-[68px]">
                    {formatTime(entry.timestamp)}
                </span>

                {/* Level icon */}
                <Icon
                    size={12}
                    className="shrink-0 mt-[2px] theme-accent-text"
                    style={accentStyle}
                />

                {/* Source badge */}
                <span
                    className="text-[9px] font-medium px-1 py-0 rounded shrink-0 mt-[1px] theme-accent-chip"
                    style={accentStyle}
                >
                    {t(`log.source.${entry.source}`)}
                </span>

                {/* Unit ID badge */}
                {entry.unitId && (
                    <span
                        className="text-[9px] font-mono px-1 py-0 rounded shrink-0 mt-[1px] theme-accent-chip"
                        style={getThemeAccentStyle("refScope")}
                    >
                        {entry.unitId}
                    </span>
                )}

                {/* Message */}
                <span
                    className={`text-[11px] flex-1 min-w-0 ${entry.level === "error"
                        ? "theme-accent-text"
                        : "text-foreground/80"
                        }`}
                    style={entry.level === "error" ? errorAccentStyle : undefined}
                >
                    {entry.message}
                </span>

                {/* Repeat count badge */}
                {entry.repeatCount > 1 && (
                    <span
                        className="text-[9px] font-mono px-1 py-0 rounded shrink-0 mt-[1px] theme-accent-chip"
                        style={getThemeAccentStyle("warning")}
                    >
                        ×{entry.repeatCount}
                    </span>
                )}

                {/* Expand indicator */}
                {hasExtra && (
                    <span className="text-[10px] text-muted-foreground/40 shrink-0 mt-[2px]">
                        {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                    </span>
                )}
            </button>

            {/* Expanded details */}
            {expanded && hasExtra ? (
                <div className="px-3 pb-2 ml-[80px]">
                    {entry.stack ? (
                        <pre
                            className="text-[10px] font-mono whitespace-pre-wrap break-all rounded p-2 mb-1 max-h-40 overflow-auto theme-accent-banner"
                            style={errorAccentStyle}
                        >
                            {entry.stack}
                        </pre>
                    ) : null}
                    {entry.data != null ? (
                        <pre className="text-[10px] font-mono text-muted-foreground/60 whitespace-pre-wrap break-all bg-muted/30 rounded p-2 max-h-32 overflow-auto">
                            {JSON.stringify(entry.data, null, 2)}
                        </pre>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
